import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Table,
  Tooltip,
} from "components/ui";
import {
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineMail,
  HiOutlineDeviceMobile,
  HiOutlineChatAlt2,
  HiOutlineBell,
  HiChevronDown,
} from "react-icons/hi";
import classNames from "classnames";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

const { THead, TBody, Tr, Th, Td } = Table;

export const CHANNELS = [
  { value: "email_to", label: "Email (To)", short: "Email (To)" },
  { value: "email_cc", label: "Email (CC)", short: "Email (CC)" },
  { value: "email_bcc", label: "Email (BCC)", short: "Email (BCC)" },
  { value: "sms", label: "SMS", short: "SMS" },
  // { value: "push_notification", label: "Push Notification", short: "Push" },
  // { value: "genni_chat", label: "Genni Chat", short: "Chat" },
];

export const isEmailChannel = (channel) =>
  channel === "email_to" || channel === "email_cc" || channel === "email_bcc";

export const isPhoneChannel = (channel) => channel === "sms";

export const isNotificationChannel = (channel) =>
  channel === "push_notification" || channel === "genni_chat";

export const channelLabel = (value) =>
  CHANNELS.find((channel) => channel.value === value)?.label || value;

export const newRecipientId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `r_${Math.random().toString(36).slice(2, 10)}`;

export const buildAllCommunicationsOn = (communications = []) =>
  communications.reduce((accumulator, communication) => {
    accumulator[communication.key] = true;
    return accumulator;
  }, {});

const valuePlaceholder = (channel) =>
  isPhoneChannel(channel)
    ? "(555) 123-4567"
    : channel === "push_notification"
    ? "Device or token label"
    : channel === "genni_chat"
    ? "Chat target or label"
    : "alex@example.com";

export const normalizePhone = (value) => {
  const raw = (value || "").trim();
  if (!raw) return "";

  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
};

export const formatPhoneForDisplay = (value) => {
  if (!value) return "";
  if (!value.startsWith("+")) return value;

  const digits = value.slice(1);
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
      7,
    )}`;
  }

  return value;
};

const validateValue = (channel, value) => {
  if (!value || !value.trim()) return "Required";

  if (isEmailChannel(channel)) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
      ? null
      : "Invalid email";
  }

  if (isPhoneChannel(channel)) {
    return isValidPhoneNumber(value) ? null : "Enter a valid phone number";
  }

  if (isNotificationChannel(channel)) {
    return value.trim() ? null : "Required";
  }

  return null;
};

const isReadyForPhoneValidation = (value) =>
  (value || "").replace(/\D/g, "").length >= 7;

const getShortLabel = (communication) => {
  if (communication.short) {
    return communication.short;
  }

  const title = communication.title || communication.full || communication.key;
  if (!title) {
    return "";
  }

  return title.length > 20 ? `${title.slice(0, 20)}...` : title;
};

const getStoredValue = (channel, value) =>
  isPhoneChannel(channel) ? normalizePhone(value) : value.trim();

const getDisplayValue = (channel, value) =>
  isPhoneChannel(channel) ? formatPhoneForDisplay(value) : value;

const PhoneField = ({ value, onChange, onKeyDown, autoFocus, invalid }) => (
  <PhoneInput
    international
    defaultCountry="US"
    countryCallingCodeEditable={false}
    value={value ? value : undefined}
    onChange={(next) => onChange(next || "")}
    numberInputProps={{
      onKeyDown,
      autoFocus,
      className: "notifications-phone-input__number",
    }}
    countrySelectProps={{
      className: "notifications-phone-input__country",
    }}
    className={classNames(
      "notifications-phone-input input input-sm h-9",
      invalid && "input-invalid",
    )}
  />
);

const getTriggerSourceStyles = (source) => {
  if (source === "override_addition") {
    return {
      className:
        "rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10",
      label: "Added for this campaign",
    };
  }

  if (source === "override_deletion") {
    return {
      className:
        "rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
      label: "Removed for this campaign",
    };
  }

  return null;
};

const BADGE_STYLES = {
  sky: "border border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200",
  emerald:
    "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
  amber:
    "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200",
  gray: "border border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
};

const BadgePill = ({ text, tone = "gray", className = "" }) => (
  <span
    className={classNames(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
      BADGE_STYLES[tone] || BADGE_STYLES.gray,
      className,
    )}
  >
    {text}
  </span>
);

const NotificationsEditor = ({
  communications = [],
  recipients,
  onChange,
  recipientsDescription = "Emails, phone numbers, and channels that receive notifications.",
  triggersDescription = "Choose which communications each recipient should receive.",
}) => {
  const [editingId, setEditingId] = useState(null);
  const [addingChannel, setAddingChannel] = useState(null);
  const [draft, setDraft] = useState({ value: "" });
  const [draftError, setDraftError] = useState(null);

  const setRecipients = (updater) => {
    const next = typeof updater === "function" ? updater(recipients) : updater;
    onChange(next);
  };

  const updateRecipient = (id, updater) =>
    setRecipients((previousRecipients) =>
      previousRecipients.map((recipient) =>
        recipient.id === id ? updater(recipient) : recipient,
      ),
    );

  const removeRecipient = (id) => {
    setRecipients((previousRecipients) =>
      previousRecipients.filter((recipient) => recipient.id !== id),
    );
    if (editingId === id) {
      setEditingId(null);
    }
  };

  const toggleCell = (recipientId, communicationKey) =>
    updateRecipient(recipientId, (recipient) => ({
      ...recipient,
      triggers: {
        ...recipient.triggers,
        [communicationKey]: !recipient.triggers?.[communicationKey],
      },
    }));

  const columnState = useMemo(() => {
    const state = {};

    communications.forEach((communication) => {
      if (recipients.length === 0) {
        state[communication.key] = "none";
        return;
      }

      const enabledCount = recipients.filter(
        (recipient) => recipient.triggers?.[communication.key],
      ).length;

      state[communication.key] =
        enabledCount === 0
          ? "none"
          : enabledCount === recipients.length
          ? "all"
          : "some";
    });

    return state;
  }, [communications, recipients]);

  const clientColumnStartIndex = useMemo(
    () => communications.findIndex((communication) => communication.audience === "client"),
    [communications],
  );
  const connectedPartnerColumnCount =
    clientColumnStartIndex === -1 ? communications.length : clientColumnStartIndex;
  const clientColumnCount =
    clientColumnStartIndex === -1 ? 0 : communications.length - clientColumnStartIndex;

  const toggleColumn = (communicationKey) => {
    const nextValue = columnState[communicationKey] !== "all";
    setRecipients((previousRecipients) =>
      previousRecipients.map((recipient) => ({
        ...recipient,
        triggers: {
          ...recipient.triggers,
          [communicationKey]: nextValue,
        },
      })),
    );
  };

  const startAdd = (channel) => {
    setAddingChannel(channel);
    setDraft({ value: "" });
    setDraftError(null);
    setEditingId(null);
  };

  const cancelAdd = () => {
    setAddingChannel(null);
    setDraft({ value: "" });
    setDraftError(null);
  };

  const confirmAdd = () => {
    const error = validateValue(addingChannel, draft.value);
    if (error) {
      setDraftError(error);
      return;
    }

    setRecipients((previousRecipients) => [
      ...previousRecipients,
      {
        id: newRecipientId(),
        channel: addingChannel,
        value: getStoredValue(addingChannel, draft.value),
        label: null,
        triggers: buildAllCommunicationsOn(communications),
      },
    ]);

    cancelAdd();
  };

  const hasRecipientContent = recipients.length > 0 || !!addingChannel;

  return (
    <>
      <section>
        <SectionHeader
          title="Recipients"
          description={recipientsDescription}
          action={
            hasRecipientContent ? <AddRecipientMenu onPick={startAdd} /> : null
          }
        />
        <div className="mt-5">
          {hasRecipientContent ? (
            <RecipientsTable
              recipients={recipients}
              editingId={editingId}
              addingChannel={addingChannel}
              draft={draft}
              draftError={draftError}
              onStartEdit={(id) => setEditingId(id)}
              onCancelEdit={() => setEditingId(null)}
              onCommitEdit={(recipient, nextRecipient) => {
                updateRecipient(recipient.id, () => nextRecipient);
                setEditingId(null);
              }}
              onRemove={removeRecipient}
              onDraftChange={(nextDraft) => {
                setDraft(nextDraft);

                if (!nextDraft.value || !nextDraft.value.trim()) {
                  setDraftError(null);
                  return;
                }

                if (
                  isPhoneChannel(addingChannel) &&
                  !isReadyForPhoneValidation(nextDraft.value)
                ) {
                  setDraftError(null);
                  return;
                }

                setDraftError(validateValue(addingChannel, nextDraft.value));
              }}
              onCancelAdd={cancelAdd}
              onConfirmAdd={confirmAdd}
            />
          ) : (
            <EmptyState onPick={startAdd} />
          )}
        </div>
      </section>

      <section className="mt-12">
        <SectionHeader
          title="Communication Events"
          description={triggersDescription}
        />
        <div className="mt-5">
          {!communications.length ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No communication events are available for this view.
            </p>
          ) : recipients.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Add a recipient above to configure communication events.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table compact>
                <THead>
                  {clientColumnCount > 0 && (
                    <Tr>
                      <Th />
                      {connectedPartnerColumnCount > 0 && (
                        <Th
                          colSpan={connectedPartnerColumnCount}
                          className="border-b-0 px-2 pb-1 pt-2 text-left"
                        >
                          <span className="inline-block rounded-sm px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-600 dark:text-emerald-300">
                            Connected Partner Events
                          </span>
                        </Th>
                      )}
                      <Th
                        colSpan={clientColumnCount}
                        className="border-b-0 border-l border-sky-200 bg-sky-50/60 px-2 pb-1 pt-2 text-left dark:border-sky-500/30 dark:bg-sky-500/5"
                      >
                        <span className="ml-1 inline-block rounded-sm px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-200">
                          Client Events
                        </span>
                      </Th>
                    </Tr>
                  )}
                  <Tr>
                    <Th className="px-3 py-3">Recipient</Th>
                    {communications.map((communication) => (
                      <Th
                        key={communication.key}
                        className={classNames(
                          "min-w-[92px] max-w-[110px] px-3 py-3 text-center align-top",
                          communication.audience === "client" &&
                            "border-l border-sky-200 bg-sky-50/40 dark:border-sky-500/30 dark:bg-sky-500/5",
                        )}
                      >
                        <div className="flex h-[48px] items-start justify-center">
                          <Tooltip
                            title={
                              <CommunicationTooltip
                                communication={communication}
                              />
                            }
                          >
                            <button
                              type="button"
                              onClick={() => toggleColumn(communication.key)}
                              className="flex w-full items-start justify-center px-1 text-center text-[11px] leading-[1.2] font-medium whitespace-normal hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                            >
                              {getShortLabel(communication)}
                            </button>
                          </Tooltip>
                        </div>
                      </Th>
                    ))}
                  </Tr>
                </THead>
                <TBody>
                  {recipients.map((recipient) => (
                    <Tr key={recipient.id}>
                      <Td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <ChannelIcon channel={recipient.channel} />
                          <div className="min-w-0 max-w-[220px]">
                            <div className="font-mono text-xs text-gray-800 dark:text-gray-100 break-all">
                              {getDisplayValue(
                                recipient.channel,
                                recipient.value,
                              )}
                            </div>
                            {(recipient.label || recipient.badges?.length > 0) && (
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                {recipient.label && (
                                  <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                    {recipient.label}
                                  </div>
                                )}
                                {(recipient.badges || []).map((badge) => (
                                  <BadgePill
                                    key={`${recipient.id}-${badge.text}`}
                                    text={badge.text}
                                    tone={badge.tone}
                                    className="text-[9px]"
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Td>
                      {communications.map((communication) => {
                        const triggerSourceStyle = getTriggerSourceStyles(
                          recipient.triggerSources?.[communication.key],
                        );

                        return (
                          <Td
                            key={communication.key}
                            className={classNames(
                              "px-3 py-3 align-middle",
                              communication.audience === "client" &&
                                "border-l border-sky-100 bg-sky-50/30 dark:border-sky-500/20 dark:bg-sky-500/5",
                            )}
                          >
                            <div className="flex w-full justify-center">
                              {triggerSourceStyle ? (
                                <Tooltip
                                  title={triggerSourceStyle?.label || ""}
                                >
                                  <div
                                    className={classNames(
                                      "flex w-9 min-h-12 items-start justify-center rounded-md pt-2 pb-2",
                                      triggerSourceStyle?.className,
                                    )}
                                  >
                                    <Checkbox
                                      checked={
                                        !!recipient.triggers?.[
                                          communication.key
                                        ]
                                      }
                                      onChange={() =>
                                        toggleCell(
                                          recipient.id,
                                          communication.key,
                                        )
                                      }
                                      className="m-0 flex items-center justify-center"
                                    />
                                  </div>
                                </Tooltip>
                              ) : (
                                <div
                                  className={classNames(
                                    "flex w-9 min-h-12 items-start justify-center rounded-md pt-2 pb-2",
                                    triggerSourceStyle?.className,
                                  )}
                                >
                                  <Checkbox
                                    checked={
                                      !!recipient.triggers?.[communication.key]
                                    }
                                    onChange={() =>
                                      toggleCell(
                                        recipient.id,
                                        communication.key,
                                      )
                                    }
                                    className="m-0 flex items-center justify-center"
                                  />
                                </div>
                              )}
                            </div>
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const CommunicationTooltip = ({ communication }) => (
  <div className="max-w-[260px] text-left">
    <div className="font-semibold mb-1">
      {communication.title || communication.full}
    </div>
    {communication.audience === "client" && (
      <div className="mb-1 text-[11px] font-medium text-sky-500 dark:text-sky-200">
        Client event
      </div>
    )}
    {communication.audience === "connected_partner" && (
      <div className="mb-1 text-[11px] font-medium text-emerald-500 dark:text-emerald-200">
        Connected partner event
      </div>
    )}
    {communication.audience === "shared" && (
      <div className="mb-1 text-[11px] font-medium text-amber-500 dark:text-amber-200">
        Shared event
      </div>
    )}
    {communication.description && (
      <div className="text-xs leading-snug opacity-90">
        {communication.description}
      </div>
    )}
    <div className="text-[10px] uppercase tracking-wide opacity-60 mt-1.5">
      Click to toggle for all recipients
    </div>
  </div>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <h5 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h5>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {description}
        </p>
      )}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

const AddRecipientMenu = ({ onPick }) => (
  <Dropdown
    placement="bottom-end"
    renderTitle={
      <Button size="sm" variant="solid" icon={<HiOutlinePlus />}>
        <span className="inline-flex items-center gap-1">
          Add recipient <HiChevronDown size={14} />
        </span>
      </Button>
    }
  >
    {CHANNELS.map((channel) => (
      <Dropdown.Item
        key={channel.value}
        eventKey={channel.value}
        onSelect={() => onPick(channel.value)}
      >
        <div className="flex items-center gap-2">
          <ChannelIcon channel={channel.value} />
          <span>{channel.label}</span>
        </div>
      </Dropdown.Item>
    ))}
  </Dropdown>
);

const EmptyState = ({ onPick }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg py-14 px-6 text-center">
    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
      <HiOutlineBell className="text-gray-400" size={20} />
    </div>
    <h6 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
      No recipients yet
    </h6>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5 max-w-sm mx-auto">
      Add an email address, phone number, or channel to start sending
      notifications.
    </p>
    <AddRecipientMenu onPick={onPick} />
  </div>
);

const RecipientsTable = ({
  recipients,
  editingId,
  addingChannel,
  draft,
  draftError,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onRemove,
  onDraftChange,
  onCancelAdd,
  onConfirmAdd,
}) => (
  <Table compact>
    <THead>
      <Tr>
        <Th className="w-[160px]">Channel</Th>
        <Th>Recipient</Th>
        <Th className="w-[120px]" />
      </Tr>
    </THead>
    <TBody>
      {recipients.map((recipient) =>
        editingId === recipient.id ? (
          <RecipientEditRow
            key={recipient.id}
            recipient={recipient}
            onCommit={(nextRecipient) => onCommitEdit(recipient, nextRecipient)}
            onCancel={onCancelEdit}
            onRemove={() => onRemove(recipient.id)}
          />
        ) : (
          <RecipientDisplayRow
            key={recipient.id}
            recipient={recipient}
            onEdit={() => onStartEdit(recipient.id)}
            onRemove={() => onRemove(recipient.id)}
          />
        ),
      )}
      {addingChannel && (
        <NewRecipientRow
          channel={addingChannel}
          draft={draft}
          draftError={draftError}
          onDraftChange={onDraftChange}
          onCancel={onCancelAdd}
          onConfirm={onConfirmAdd}
        />
      )}
    </TBody>
  </Table>
);

const ChannelIcon = ({ channel }) => {
  let Icon = HiOutlineMail;

  if (channel === "sms" || channel === "whatsapp") {
    Icon = channel === "whatsapp" ? HiOutlineChatAlt2 : HiOutlineDeviceMobile;
  } else if (channel === "push_notification") {
    Icon = HiOutlineBell;
  } else if (channel === "genni_chat") {
    Icon = HiOutlineChatAlt2;
  }

  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 shrink-0">
      <Icon size={14} />
    </span>
  );
};

const ChannelCell = ({ channel }) => (
  <div className="flex items-center gap-4">
    <ChannelIcon channel={channel} />
    <span className="text-sm text-gray-700 dark:text-gray-200">
      {channelLabel(channel)}
    </span>
  </div>
);

const RecipientDisplayRow = ({ recipient, onEdit, onRemove }) => (
  <Tr>
    <Td>
      <ChannelCell channel={recipient.channel} />
    </Td>
    <Td className="font-mono text-sm text-gray-800 dark:text-gray-100">
      {getDisplayValue(recipient.channel, recipient.value)}
    </Td>
    <Td>
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Edit
        </button>
        <button
          type="button"
          className="text-gray-400 hover:text-red-500 transition-colors"
          onClick={onRemove}
          aria-label="Remove recipient"
        >
          <HiOutlineTrash size={16} />
        </button>
      </div>
    </Td>
  </Tr>
);

const RecipientEditRow = ({ recipient, onCommit, onCancel, onRemove }) => {
  const isPhone = isPhoneChannel(recipient.channel);
  const [value, setValue] = useState(
    isPhone ? normalizePhone(recipient.value) : recipient.value || "",
  );
  const [error, setError] = useState(null);

  const handleChange = (nextValue) => {
    setValue(nextValue);

    if (!nextValue || !String(nextValue).trim()) {
      setError(null);
      return;
    }

    if (isPhone && !isReadyForPhoneValidation(nextValue)) {
      setError(null);
      return;
    }

    setError(validateValue(recipient.channel, nextValue));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") commit();
    if (event.key === "Escape") onCancel();
  };

  const commit = () => {
    const nextError = validateValue(recipient.channel, value);
    if (nextError) {
      setError(nextError);
      return;
    }

    onCommit({ ...recipient, value: getStoredValue(recipient.channel, value) });
  };

  return (
    <Tr>
      <Td>
        <ChannelCell channel={recipient.channel} />
      </Td>
      <Td>
        {isPhone ? (
          <PhoneField
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            invalid={!!error}
          />
        ) : (
          <Input
            size="sm"
            value={value}
            placeholder={valuePlaceholder(recipient.channel)}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={handleKeyDown}
            invalid={!!error}
          />
        )}
        {error && (
          <span className="text-[11px] text-red-500 mt-1 block">{error}</span>
        )}
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-2">
          <Button size="xs" variant="plain" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="xs" variant="solid" onClick={commit} disabled={!!error}>
            Save
          </Button>
          <button
            type="button"
            className="text-gray-400 hover:text-red-500 transition-colors"
            onClick={onRemove}
            aria-label="Remove recipient"
          >
            <HiOutlineTrash size={16} />
          </button>
        </div>
      </Td>
    </Tr>
  );
};

const NewRecipientRow = ({
  channel,
  draft,
  draftError,
  onDraftChange,
  onCancel,
  onConfirm,
}) => {
  const isPhone = isPhoneChannel(channel);
  const handleKeyDown = (event) => {
    if (event.key === "Enter") onConfirm();
    if (event.key === "Escape") onCancel();
  };

  return (
    <Tr>
      <Td>
        <ChannelCell channel={channel} />
      </Td>
      <Td>
        {isPhone ? (
          <PhoneField
            value={draft.value}
            onChange={(nextValue) =>
              onDraftChange({ ...draft, value: nextValue })
            }
            onKeyDown={handleKeyDown}
            autoFocus
            invalid={!!draftError}
          />
        ) : (
          <Input
            size="sm"
            autoFocus
            value={draft.value}
            placeholder={valuePlaceholder(channel)}
            onChange={(event) =>
              onDraftChange({ ...draft, value: event.target.value })
            }
            onKeyDown={handleKeyDown}
            invalid={!!draftError}
          />
        )}
        {draftError && (
          <span className="text-[11px] text-red-500 mt-1 block">
            {draftError}
          </span>
        )}
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-2">
          <Button size="xs" variant="plain" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="xs"
            variant="solid"
            onClick={onConfirm}
            disabled={!!draftError}
          >
            Add
          </Button>
        </div>
      </Td>
    </Tr>
  );
};

export default NotificationsEditor;
